import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Mic, Send, X, Check, Edit2, Loader2, Calendar, CheckSquare, Rocket, Clock, StopCircle, FolderPlus, BarChart3, Palmtree, Play, FileText, Users, Search, Trophy, Phone, MapPin, Ruler, Eye, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const AIAssistant = () => {
    const { api } = useAuth();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [input, setInput] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [result, setResult] = useState(null);
    const [step, setStep] = useState('input'); // input, confirm, success, loading, multi_edit
    const [multiActions, setMultiActions] = useState([]); // Editable list of actions for multi_action
    const [editingActionIndex, setEditingActionIndex] = useState(null); // Which action is being edited
    const [availableCalendars, setAvailableCalendars] = useState([]); // For calendar selection
    const [selectedFace, setSelectedFace] = useState(null); // For Social Face detail popup
    const inputRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);

    useEffect(() => {
        // Don't auto-focus on mobile to prevent keyboard from opening
        const isMobile = window.innerWidth < 768;
        if (isOpen && inputRef.current && !isMobile) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    const handleOpen = () => {
        setIsOpen(true);
        setStep('input');
        setResult(null);
        setInput('');
    };

    const handleClose = () => {
        setIsOpen(false);
        setStep('input');
        setResult(null);
        setInput('');
        stopRecording();
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
            audioChunksRef.current = [];

            mediaRecorderRef.current.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorderRef.current.onstop = async () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                stream.getTracks().forEach(track => track.stop());
                await transcribeAudio(audioBlob);
            };

            mediaRecorderRef.current.start();
            setIsRecording(true);
        } catch (error) {
            console.error('Microphone access denied:', error);
            toast.error('Mikrofonzugriff verweigert');
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
        }
    };

    const transcribeAudio = async (audioBlob) => {
        setIsProcessing(true);
        try {
            const formData = new FormData();
            formData.append('file', audioBlob, 'recording.webm');

            const response = await api.post('/ai-assistant/transcribe', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response.data.success && response.data.text) {
                setInput(response.data.text);
                // Auto-process the transcribed text
                await processInput(response.data.text);
            } else {
                toast.error('Spracherkennung fehlgeschlagen');
            }
        } catch (error) {
            console.error('Transcription error:', error);
            toast.error('Fehler bei der Spracherkennung');
        } finally {
            setIsProcessing(false);
        }
    };

    const processInput = async (text = input) => {
        if (!text.trim()) return;

        setIsProcessing(true);
        try {
            const response = await api.post('/ai-assistant/process', { text });
            
            if (response.data.success) {
                // Actions that execute immediately without confirmation
                const immediateActions = [
                    'report', 'query_tasks', 'query_calendar', 'query_projects', 
                    'query_vacation', 'query_team', 'summary', 'search', 'gamification'
                ];
                
                // Time tracking start/stop also immediate
                const isTimeTrackingImmediate = response.data.action === 'time_tracking' && 
                    ['start', 'stop'].includes(response.data.data?.operation);
                
                // Actions that need user selection (like calendar choice)
                const selectionActions = ['clarify_calendar'];
                
                if (immediateActions.includes(response.data.action) || isTimeTrackingImmediate) {
                    const execResponse = await api.post('/ai-assistant/execute', {
                        action: response.data.action,
                        data: response.data.data
                    });
                    setResult({
                        ...response.data,
                        executed: true,
                        execResult: execResponse.data
                    });
                    setStep('confirm');
                } else if (selectionActions.includes(response.data.action)) {
                    // Show selection UI
                    setResult(response.data);
                    setStep('confirm');
                } else if (response.data.action === 'multi_action') {
                    // Multi-action: prepare editable actions and load calendars if needed
                    const actions = response.data.data?.actions || [];
                    setMultiActions(actions.map(a => ({ ...a, confirmed: false })));
                    setResult(response.data);
                    
                    // Check if any event needs calendar - load available calendars
                    const hasEvents = actions.some(a => a.action === 'create_event');
                    if (hasEvents) {
                        try {
                            const calSettings = await api.get('/calendar/settings');
                            if (calSettings.data.connected_calendars?.length > 0) {
                                setAvailableCalendars(calSettings.data.connected_calendars);
                            }
                        } catch (e) {
                            console.error('Could not load calendars:', e);
                        }
                    }
                    
                    setStep('confirm');
                } else {
                    setResult(response.data);
                    setStep('confirm');
                }
            } else {
                setResult(response.data);
                if (response.data.action === 'unknown') {
                    setStep('confirm');
                } else {
                    toast.error(response.data.message);
                }
            }
        } catch (error) {
            console.error('Processing error:', error);
            toast.error('Fehler beim Verarbeiten');
        } finally {
            setIsProcessing(false);
        }
    };

    const executeAction = async () => {
        if (!result) return;

        setIsProcessing(true);
        try {
            const response = await api.post('/ai-assistant/execute', {
                action: result.action,
                data: result.data
            });

            if (response.data.success) {
                setStep('success');
                toast.success(response.data.message);
                setTimeout(() => {
                    handleClose();
                }, 2000);
            } else {
                toast.error(response.data.message || 'Fehler beim Speichern');
            }
        } catch (error) {
            console.error('Execute error:', error);
            toast.error('Fehler beim Erstellen');
        } finally {
            setIsProcessing(false);
        }
    };

    // Execute all multi-actions
    const executeMultiActions = async () => {
        if (!multiActions.length) return;

        setIsProcessing(true);
        setStep('loading');
        
        let successCount = 0;
        let errorCount = 0;

        for (const action of multiActions) {
            try {
                const response = await api.post('/ai-assistant/execute', {
                    action: action.action,
                    data: action.data
                });
                if (response.data.success) {
                    successCount++;
                } else {
                    errorCount++;
                }
            } catch (error) {
                console.error('Execute error:', error);
                errorCount++;
            }
        }

        setIsProcessing(false);
        
        if (errorCount === 0) {
            setStep('success');
            toast.success(`${successCount} Aktionen erfolgreich erstellt!`);
            setTimeout(() => {
                handleClose();
            }, 2000);
        } else {
            toast.error(`${successCount} erstellt, ${errorCount} fehlgeschlagen`);
            setStep('input');
        }
    };

    // Update a specific action in multi-actions
    const updateMultiAction = (index, field, value) => {
        setMultiActions(prev => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                data: {
                    ...updated[index].data,
                    [field]: value
                }
            };
            return updated;
        });
    };

    // Remove an action from multi-actions
    const removeMultiAction = (index) => {
        setMultiActions(prev => prev.filter((_, i) => i !== index));
    };

    // Edit single action from multi-action (navigate to page)
    const editSingleAction = (action, index) => {
        if (action.action === 'create_task') {
            const prefillData = {
                title: action.data.title || '',
                description: action.data.description || '',
                assigned_to: action.data.assigned_to || '',
                deadline: action.data.deadline || '',
                priority: action.data.priority === 'high' ? 1 : action.data.priority === 'low' ? 3 : 2
            };
            handleClose();
            navigate('/tasks');
            setTimeout(() => {
                window.dispatchEvent(new CustomEvent('openTaskModal', { 
                    detail: { prefill: prefillData }
                }));
            }, 500);
        } else if (action.action === 'create_event') {
            const prefillData = {
                title: action.data.title || '',
                description: action.data.description || '',
                start_date: action.data.start_date || '',
                start_time: action.data.start_time || '',
                end_time: action.data.end_time || '',
                location: action.data.location || ''
            };
            handleClose();
            navigate('/calendar');
            setTimeout(() => {
                window.dispatchEvent(new CustomEvent('openEventModal', { 
                    detail: { prefill: prefillData }
                }));
            }, 500);
        }
    };

    const handleEdit = () => {
        // Navigate to the appropriate page and dispatch event after a short delay
        if (result?.action === 'create_task') {
            const prefillData = {
                title: result.data.title || '',
                description: result.data.description || '',
                assigned_to: result.data.assigned_to || '',
                deadline: result.data.deadline || '',
                priority: result.data.priority === 'high' ? 1 : result.data.priority === 'low' ? 3 : 2
            };
            handleClose();
            // Navigate to tasks page first, then open modal
            navigate('/tasks');
            // Dispatch event after navigation
            setTimeout(() => {
                window.dispatchEvent(new CustomEvent('openTaskModal', { 
                    detail: { prefill: prefillData }
                }));
            }, 500);
        } else if (result?.action === 'create_event') {
            const prefillData = {
                title: result.data.title || '',
                description: result.data.description || '',
                start_date: result.data.start_date || '',
                start_time: result.data.start_time || '',
                end_time: result.data.end_time || '',
                location: result.data.location || ''
            };
            handleClose();
            // Navigate to calendar page first, then open modal
            navigate('/calendar');
            // Dispatch event after navigation
            setTimeout(() => {
                window.dispatchEvent(new CustomEvent('openEventModal', { 
                    detail: { prefill: prefillData }
                }));
            }, 500);
        } else {
            // Fallback: go back to input
            setStep('input');
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            processInput();
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        try {
            return format(new Date(dateStr), 'EEEE, d. MMMM yyyy', { locale: de });
        } catch {
            return dateStr;
        }
    };

    return (
        <>
            {/* Floating Assistant Button */}
            <motion.div
                className="fixed z-50 flex flex-col items-center"
                style={{ 
                    bottom: 'max(1.5rem, calc(env(safe-area-inset-bottom) + 1.5rem))',
                    right: '1.5rem'
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
            >
                {/* Outer pulsing rings - pure CSS for smooth animation */}
                <div 
                    className="absolute w-14 h-14 rounded-full pointer-events-none"
                    style={{
                        border: '2px solid hsl(199, 100%, 44%)',
                        animation: 'pulse-ring 3s ease-out infinite'
                    }}
                />
                <div 
                    className="absolute w-14 h-14 rounded-full pointer-events-none"
                    style={{
                        border: '2px solid hsl(199, 100%, 44%)',
                        animation: 'pulse-ring 3s ease-out infinite 1.5s'
                    }}
                />
                <style>{`
                    @keyframes pulse-ring {
                        0% {
                            transform: scale(1);
                            opacity: 0.4;
                        }
                        100% {
                            transform: scale(1.6);
                            opacity: 0;
                        }
                    }
                `}</style>
                
                {/* Main Button */}
                <motion.button
                    onClick={handleOpen}
                    className="relative w-14 h-14 rounded-full shadow-xl flex items-center justify-center text-white overflow-hidden"
                    style={{
                        background: 'linear-gradient(135deg, hsl(199, 100%, 50%) 0%, hsl(199, 100%, 40%) 100%)'
                    }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    data-testid="ai-assistant-button"
                >
                    {/* Icon - Rocket with gentle float animation */}
                    <motion.div
                        animate={{ 
                            y: [0, -2, 0],
                        }}
                        transition={{ 
                            duration: 3,
                            repeat: Infinity,
                            ease: "easeInOut"
                        }}
                        className="relative z-10"
                    >
                        <Rocket className="w-6 h-6" />
                    </motion.div>
                </motion.button>
            </motion.div>

            {/* Assistant Popup */}
            <AnimatePresence>
                {isOpen && (
                    <>
                        {/* Backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
                            onClick={handleClose}
                        />

                        {/* Popup */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                            className="fixed right-6 z-50 w-[400px] max-w-[calc(100vw-48px)] bg-background/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-border/50 overflow-hidden"
                            style={{ 
                                bottom: 'max(6rem, calc(env(safe-area-inset-bottom) + 6rem))'
                            }}
                            data-testid="ai-assistant-popup"
                        >
                            {/* Header - bläulicher Hintergrund */}
                            <div className="px-5 py-4 border-b border-border/50 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, hsla(199, 100%, 44%, 0.15) 0%, hsla(199, 100%, 44%, 0.05) 100%)' }}>
                                <div className="flex items-center gap-3">
                                    <motion.div 
                                        className="w-10 h-10 rounded-full flex items-center justify-center"
                                        style={{ background: 'linear-gradient(135deg, hsl(199, 100%, 50%) 0%, hsl(199, 100%, 40%) 100%)' }}
                                        animate={{ 
                                            boxShadow: [
                                                '0 0 0 0 hsla(199, 100%, 44%, 0)',
                                                '0 0 0 8px hsla(199, 100%, 44%, 0.25)',
                                                '0 0 0 0 hsla(199, 100%, 44%, 0)'
                                            ]
                                        }}
                                        transition={{ duration: 2, repeat: Infinity }}
                                    >
                                        <Rocket className="w-5 h-5 text-white" />
                                    </motion.div>
                                    <div>
                                        <h3 className="font-semibold text-sm">AgencyOS Assistent</h3>
                                        <p className="text-xs text-muted-foreground">Sag einfach, was erledigt werden soll.</p>
                                    </div>
                                </div>
                                <Button variant="ghost" size="icon" onClick={handleClose} className="h-8 w-8 hover:bg-red-500/10 hover:text-red-500 transition-colors">
                                    <X className="w-4 h-4" />
                                </Button>
                            </div>

                            {/* Content */}
                            <div className="p-5">
                                <AnimatePresence mode="wait">
                                    {step === 'input' && (
                                        <motion.div
                                            key="input"
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: 20 }}
                                        >
                                            <div className="relative">
                                                <Input
                                                    ref={inputRef}
                                                    value={input}
                                                    onChange={(e) => setInput(e.target.value)}
                                                    onKeyDown={handleKeyDown}
                                                    placeholder="z.B. Erstelle einen Task für Website Design morgen..."
                                                    className="pr-24 py-6 text-base rounded-xl bg-muted/50 border-0 focus-visible:ring-2 focus-visible:ring-primary/50"
                                                    disabled={isProcessing}
                                                    data-testid="ai-input"
                                                />
                                                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                                                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                                        <Button
                                                            size="icon"
                                                            variant={isRecording ? "default" : "ghost"}
                                                            className={`h-9 w-9 rounded-full transition-all duration-300 ${
                                                                isRecording 
                                                                    ? 'bg-green-500 hover:bg-green-600 text-white shadow-lg shadow-green-500/30' 
                                                                    : 'hover:bg-blue-500/10 hover:text-blue-500'
                                                            }`}
                                                            onClick={isRecording ? stopRecording : startRecording}
                                                            disabled={isProcessing}
                                                            data-testid="mic-button"
                                                        >
                                                            {isRecording ? (
                                                                <motion.div
                                                                    initial={{ scale: 0 }}
                                                                    animate={{ scale: 1 }}
                                                                    transition={{ type: 'spring', stiffness: 500 }}
                                                                >
                                                                    <Check className="w-4 h-4" />
                                                                </motion.div>
                                                            ) : (
                                                                <Mic className="w-4 h-4" />
                                                            )}
                                                        </Button>
                                                    </motion.div>
                                                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                                        <Button
                                                            size="icon"
                                                            className="h-9 w-9 rounded-full text-white shadow-lg"
                                                            style={{ background: 'linear-gradient(135deg, hsl(199, 100%, 50%) 0%, hsl(199, 100%, 40%) 100%)' }}
                                                            onClick={() => processInput()}
                                                            disabled={isProcessing || !input.trim()}
                                                            data-testid="send-button"
                                                        >
                                                            {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                                        </Button>
                                                    </motion.div>
                                                </div>
                                            </div>

                                            {isRecording && (
                                                <motion.div 
                                                    initial={{ opacity: 0, y: -10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    className="mt-4 flex items-center justify-center gap-3 p-3 rounded-xl bg-green-500/10 border border-green-500/20"
                                                >
                                                    <div className="flex items-center gap-1.5">
                                                        <motion.div 
                                                            className="w-2.5 h-2.5 rounded-full bg-green-500"
                                                            animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
                                                            transition={{ duration: 1, repeat: Infinity }}
                                                        />
                                                        <motion.div 
                                                            className="w-2.5 h-2.5 rounded-full bg-green-500"
                                                            animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
                                                            transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
                                                        />
                                                        <motion.div 
                                                            className="w-2.5 h-2.5 rounded-full bg-green-500"
                                                            animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
                                                            transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
                                                        />
                                                    </div>
                                                    <span className="text-sm font-medium text-green-600">Aufnahme läuft</span>
                                                    <span className="text-xs text-muted-foreground">• Klicke ✓ zum Stoppen</span>
                                                </motion.div>
                                            )}

                                            <div className="mt-4 flex flex-wrap gap-2">
                                                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                                    <Badge variant="outline" className="text-xs cursor-pointer hover:bg-blue-500/10 hover:border-blue-500/50 transition-all" onClick={() => setInput('Erstelle einen Task für ')}>
                                                        <CheckSquare className="w-3 h-3 mr-1" /> Task
                                                    </Badge>
                                                </motion.div>
                                                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                                    <Badge variant="outline" className="text-xs cursor-pointer hover:bg-purple-500/10 hover:border-purple-500/50 transition-all" onClick={() => setInput('Lege ein Meeting an ')}>
                                                        <Calendar className="w-3 h-3 mr-1" /> Meeting
                                                    </Badge>
                                                </motion.div>
                                                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                                    <Badge variant="outline" className="text-xs cursor-pointer hover:bg-green-500/10 hover:border-green-500/50 transition-all" onClick={() => setInput('Starte Zeiterfassung für ')}>
                                                        <Clock className="w-3 h-3 mr-1" /> Zeit starten
                                                    </Badge>
                                                </motion.div>
                                                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                                    <Badge variant="outline" className="text-xs cursor-pointer hover:bg-red-500/10 hover:border-red-500/50 transition-all" onClick={() => setInput('Stopp die Zeit')}>
                                                        <StopCircle className="w-3 h-3 mr-1" /> Zeit stoppen
                                                    </Badge>
                                                </motion.div>
                                                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                                    <Badge variant="outline" className="text-xs cursor-pointer hover:bg-orange-500/10 hover:border-orange-500/50 transition-all" onClick={() => setInput('Erstelle Projekt ')}>
                                                        <FolderPlus className="w-3 h-3 mr-1" /> Projekt
                                                    </Badge>
                                                </motion.div>
                                                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                                    <Badge variant="outline" className="text-xs cursor-pointer hover:bg-cyan-500/10 hover:border-cyan-500/50 transition-all" onClick={() => setInput('Zeig mir meine Stunden diese Woche')}>
                                                        <BarChart3 className="w-3 h-3 mr-1" /> Stunden
                                                    </Badge>
                                                </motion.div>
                                            </div>
                                        </motion.div>
                                    )}

                                    {step === 'confirm' && result && (
                                        <motion.div
                                            key="confirm"
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: 20 }}
                                            className="space-y-4"
                                        >
                                            {result.action === 'unknown' ? (
                                                <>
                                                    <p className="text-sm text-muted-foreground">{result.message}</p>
                                                    <div className="flex flex-wrap gap-2">
                                                        {result.data.suggestions?.map((suggestion, idx) => (
                                                            <Badge
                                                                key={idx}
                                                                variant="outline"
                                                                className="cursor-pointer hover:bg-muted"
                                                                onClick={() => {
                                                                    setInput(suggestion);
                                                                    setStep('input');
                                                                }}
                                                            >
                                                                {suggestion}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                    <Button variant="outline" onClick={() => setStep('input')} className="w-full">
                                                        Zurück
                                                    </Button>
                                                </>
                                            ) : result.action === 'clarify_calendar' ? (
                                                // Calendar selection needed
                                                <>
                                                    <div className="p-4 bg-gradient-to-r from-purple-500/10 to-blue-500/10 rounded-xl">
                                                        <div className="flex items-center gap-2 mb-3">
                                                            <Calendar className="w-5 h-5 text-purple-500" />
                                                            <span className="font-medium">{result.data.title}</span>
                                                        </div>
                                                        <p className="text-sm text-muted-foreground mb-4">{result.message}</p>
                                                        <div className="space-y-2">
                                                            {result.options?.map((calendar, idx) => (
                                                                <Button
                                                                    key={idx}
                                                                    variant="outline"
                                                                    className="w-full justify-start"
                                                                    onClick={async () => {
                                                                        // Execute with selected calendar
                                                                        setIsProcessing(true);
                                                                        try {
                                                                            const execRes = await api.post('/ai-assistant/execute', {
                                                                                action: 'create_event',
                                                                                data: {
                                                                                    ...result.data,
                                                                                    target_calendar: calendar
                                                                                }
                                                                            });
                                                                            if (execRes.data.success) {
                                                                                setStep('success');
                                                                                toast.success(execRes.data.message);
                                                                                setTimeout(() => {
                                                                                    handleClose();
                                                                                }, 2000);
                                                                            } else {
                                                                                toast.error(execRes.data.message || 'Fehler beim Erstellen');
                                                                                setStep('input');
                                                                            }
                                                                        } catch (err) {
                                                                            toast.error('Fehler beim Erstellen');
                                                                            setStep('input');
                                                                        } finally {
                                                                            setIsProcessing(false);
                                                                        }
                                                                    }}
                                                                >
                                                                    <Calendar className="w-4 h-4 mr-2" />
                                                                    {calendar}
                                                                </Button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <Button variant="ghost" onClick={() => setStep('input')} className="w-full">
                                                        Abbrechen
                                                    </Button>
                                                </>
                                            ) : result.action === 'report' || result.action === 'search_social_faces' || result.executed ? (
                                                // Report/Query/Gamification/Social Faces action - show executed result
                                                <>
                                                    <div className="p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl">
                                                        <div className="flex items-center gap-2 mb-3">
                                                            {result.action === 'report' && <BarChart3 className="w-5 h-5 text-blue-500" />}
                                                            {result.action === 'query_tasks' && <CheckSquare className="w-5 h-5 text-blue-500" />}
                                                            {result.action === 'query_calendar' && <Calendar className="w-5 h-5 text-purple-500" />}
                                                            {result.action === 'query_projects' && <FolderPlus className="w-5 h-5 text-green-500" />}
                                                            {result.action === 'query_vacation' && <Palmtree className="w-5 h-5 text-amber-500" />}
                                                            {result.action === 'summary' && <FileText className="w-5 h-5 text-indigo-500" />}
                                                            {result.action === 'search' && <Search className="w-5 h-5 text-gray-500" />}
                                                            {result.action === 'gamification' && <Trophy className="w-5 h-5 text-yellow-500" />}
                                                            {result.action === 'time_tracking' && <Clock className="w-5 h-5 text-orange-500" />}
                                                            {result.action === 'search_social_faces' && <Users className="w-5 h-5 text-pink-500" />}
                                                            <span className="text-sm font-medium text-muted-foreground">
                                                                {result.action === 'search_social_faces' ? 'Social Faces' : (result.execResult?.success ? 'Ergebnis' : 'Fehler')}
                                                            </span>
                                                        </div>
                                                        
                                                        {/* Social Faces Results */}
                                                        {result.action === 'search_social_faces' && result.data?.faces ? (
                                                            <div className="space-y-3">
                                                                {result.data.filters_applied?.length > 0 && (
                                                                    <div className="flex flex-wrap gap-1 mb-2">
                                                                        {result.data.filters_applied.map((f, i) => (
                                                                            <Badge key={i} variant="secondary" className="text-xs">{f}</Badge>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                
                                                                {result.data.faces.length === 0 ? (
                                                                    <p className="text-sm text-muted-foreground text-center py-4">
                                                                        Keine Social Faces mit diesen Kriterien gefunden.
                                                                    </p>
                                                                ) : (
                                                                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                                                                        {result.data.faces.map((face) => (
                                                                            <div 
                                                                                key={face.id} 
                                                                                className="flex items-center gap-3 p-2 rounded-lg bg-white/50 dark:bg-black/20 border border-border/50 cursor-pointer hover:bg-white/80 dark:hover:bg-black/40 transition-colors group"
                                                                                onClick={() => setSelectedFace(face)}
                                                                            >
                                                                                {face.profile_image ? (
                                                                                    <img 
                                                                                        src={face.profile_image} 
                                                                                        alt={`${face.first_name} ${face.last_name}`} 
                                                                                        className="w-12 h-12 rounded-full object-cover ring-2 ring-pink-200 dark:ring-pink-800" 
                                                                                    />
                                                                                ) : (
                                                                                    <div className="w-12 h-12 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center ring-2 ring-pink-200 dark:ring-pink-800">
                                                                                        <Users className="w-6 h-6 text-pink-500" />
                                                                                    </div>
                                                                                )}
                                                                                <div className="flex-1 min-w-0">
                                                                                    <p className="font-medium text-sm truncate group-hover:text-pink-600 dark:group-hover:text-pink-400 transition-colors">
                                                                                        {face.first_name} {face.last_name}
                                                                                    </p>
                                                                                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                                                        {face.city && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{face.city}</span>}
                                                                                        {face.hair_color && <span>• {face.hair_color}</span>}
                                                                                        {face.height && <span>• {face.height}cm</span>}
                                                                                    </div>
                                                                                </div>
                                                                                <div className="flex items-center gap-2 shrink-0">
                                                                                    {face.phone && (
                                                                                        <a 
                                                                                            href={`tel:${face.phone}`}
                                                                                            onClick={(e) => e.stopPropagation()}
                                                                                            className="px-2 py-1 rounded-md bg-green-500/10 text-green-600 dark:text-green-400 text-xs font-medium hover:bg-green-500/20 transition-colors flex items-center gap-1"
                                                                                        >
                                                                                            <Phone className="w-3 h-3" />
                                                                                            <span className="hidden sm:inline">{face.phone}</span>
                                                                                        </a>
                                                                                    )}
                                                                                    <Eye className="w-4 h-4 text-muted-foreground group-hover:text-pink-500 transition-colors" />
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                
                                                                <p className="text-xs text-muted-foreground text-center">
                                                                    {result.data.count} von {result.data.limit} angezeigt
                                                                </p>
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <p className="text-sm whitespace-pre-line">
                                                                    {result.execResult?.message || result.message}
                                                                </p>
                                                                {result.execResult?.data?.count !== undefined && (
                                                                    <p className="text-xs text-muted-foreground mt-2">
                                                                        {result.execResult.data.count} Einträge
                                                                    </p>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                    <Button variant="outline" onClick={() => setStep('input')} className="w-full">
                                                        Neue Anfrage
                                                    </Button>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="p-4 bg-muted/50 rounded-xl space-y-3">
                                                        <div className="flex items-center gap-2 text-sm font-medium">
                                                            {result.action === 'create_task' && <CheckSquare className="w-4 h-4 text-blue-500" />}
                                                            {result.action === 'create_event' && <Calendar className="w-4 h-4 text-purple-500" />}
                                                            {result.action === 'create_project' && <FolderPlus className="w-4 h-4 text-green-500" />}
                                                            {result.action === 'time_tracking' && <Clock className="w-4 h-4 text-orange-500" />}
                                                            {result.action === 'vacation_request' && <Palmtree className="w-4 h-4 text-amber-500" />}
                                                            {result.action === 'multi_action' && <FileText className="w-4 h-4 text-indigo-500" />}
                                                            
                                                            {result.action === 'create_task' && 'Task erstellen'}
                                                            {result.action === 'create_event' && 'Event erstellen'}
                                                            {result.action === 'create_project' && 'Projekt erstellen'}
                                                            {result.action === 'time_tracking' && 'Zeiterfassung'}
                                                            {result.action === 'vacation_request' && 'Urlaub beantragen'}
                                                            {result.action === 'multi_action' && `${result.data.actions?.length || 0} Aktionen`}
                                                        </div>
                                                        
                                                        <div className="space-y-2 text-sm">
                                                            {/* Task fields */}
                                                            {result.action === 'create_task' && (
                                                                <>
                                                                    <div className="flex justify-between">
                                                                        <span className="text-muted-foreground">Titel:</span>
                                                                        <span className="font-medium">{result.data.title}</span>
                                                                    </div>
                                                                    {result.data.assigned_to_name && (
                                                                        <div className="flex justify-between">
                                                                            <span className="text-muted-foreground">Zugewiesen an:</span>
                                                                            <span>{result.data.assigned_to_name}</span>
                                                                        </div>
                                                                    )}
                                                                    {result.data.deadline && (
                                                                        <div className="flex justify-between">
                                                                            <span className="text-muted-foreground">Fällig:</span>
                                                                            <span>{formatDate(result.data.deadline)}</span>
                                                                        </div>
                                                                    )}
                                                                    {result.data.priority && (
                                                                        <div className="flex justify-between">
                                                                            <span className="text-muted-foreground">Priorität:</span>
                                                                            <Badge variant={result.data.priority === 'high' ? 'destructive' : 'secondary'} className="text-xs">
                                                                                {result.data.priority === 'high' ? 'Hoch' : result.data.priority === 'low' ? 'Niedrig' : 'Mittel'}
                                                                            </Badge>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )}
                                                            
                                                            {/* Event fields */}
                                                            {result.action === 'create_event' && (
                                                                <>
                                                                    <div className="flex justify-between">
                                                                        <span className="text-muted-foreground">Titel:</span>
                                                                        <span className="font-medium">{result.data.title}</span>
                                                                    </div>
                                                                    {result.data.start_date && (
                                                                        <div className="flex justify-between">
                                                                            <span className="text-muted-foreground">Datum:</span>
                                                                            <span>{formatDate(result.data.start_date)}</span>
                                                                        </div>
                                                                    )}
                                                                    {result.data.start_time && (
                                                                        <div className="flex justify-between">
                                                                            <span className="text-muted-foreground">Zeit:</span>
                                                                            <span>{result.data.start_time} - {result.data.end_time}</span>
                                                                        </div>
                                                                    )}
                                                                    {result.data.location && (
                                                                        <div className="flex justify-between">
                                                                            <span className="text-muted-foreground">Ort:</span>
                                                                            <span>{result.data.location}</span>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )}
                                                            
                                                            {/* Project fields */}
                                                            {result.action === 'create_project' && (
                                                                <>
                                                                    <div className="flex justify-between">
                                                                        <span className="text-muted-foreground">Name:</span>
                                                                        <span className="font-medium">{result.data.name}</span>
                                                                    </div>
                                                                    {result.data.customer_name && (
                                                                        <div className="flex justify-between">
                                                                            <span className="text-muted-foreground">Kunde:</span>
                                                                            <span>{result.data.customer_name}</span>
                                                                        </div>
                                                                    )}
                                                                    {result.data.initial_tasks?.length > 0 && (
                                                                        <div>
                                                                            <span className="text-muted-foreground">Tasks:</span>
                                                                            <div className="flex flex-wrap gap-1 mt-1">
                                                                                {result.data.initial_tasks.map((t, i) => (
                                                                                    <Badge key={i} variant="outline" className="text-xs">{t}</Badge>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )}
                                                            
                                                            {/* Time tracking fields */}
                                                            {result.action === 'time_tracking' && (
                                                                <>
                                                                    <div className="flex justify-between">
                                                                        <span className="text-muted-foreground">Aktion:</span>
                                                                        <Badge className="text-xs">
                                                                            {result.data.operation === 'start' && 'Starten'}
                                                                            {result.data.operation === 'stop' && 'Stoppen'}
                                                                            {result.data.operation === 'book' && 'Buchen'}
                                                                        </Badge>
                                                                    </div>
                                                                    {result.data.project_name && (
                                                                        <div className="flex justify-between">
                                                                            <span className="text-muted-foreground">Projekt:</span>
                                                                            <span>{result.data.project_name}</span>
                                                                        </div>
                                                                    )}
                                                                    {result.data.hours > 0 && (
                                                                        <div className="flex justify-between">
                                                                            <span className="text-muted-foreground">Stunden:</span>
                                                                            <span>{result.data.hours}h</span>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )}
                                                            
                                                            {/* Vacation fields */}
                                                            {result.action === 'vacation_request' && (
                                                                <>
                                                                    <div className="flex justify-between">
                                                                        <span className="text-muted-foreground">Von:</span>
                                                                        <span>{formatDate(result.data.start_date)}</span>
                                                                    </div>
                                                                    <div className="flex justify-between">
                                                                        <span className="text-muted-foreground">Bis:</span>
                                                                        <span>{formatDate(result.data.end_date)}</span>
                                                                    </div>
                                                                </>
                                                            )}
                                                            
                                                            {/* Multi-action summary - IMPROVED */}
                                                            {result.action === 'multi_action' && multiActions.length > 0 && (
                                                                <div className="space-y-3 max-h-[350px] overflow-y-auto">
                                                                    {multiActions.map((action, idx) => (
                                                                        <div key={idx} className={`p-3 rounded-xl border-2 transition-all ${
                                                                            action.action === 'create_task' 
                                                                                ? 'border-blue-500/30 bg-blue-500/5' 
                                                                                : 'border-purple-500/30 bg-purple-500/5'
                                                                        }`}>
                                                                            {/* Action Header */}
                                                                            <div className="flex items-center justify-between mb-2">
                                                                                <div className="flex items-center gap-2">
                                                                                    {action.action === 'create_task' && <CheckSquare className="w-4 h-4 text-blue-500" />}
                                                                                    {action.action === 'create_event' && <Calendar className="w-4 h-4 text-purple-500" />}
                                                                                    <span className="text-xs font-medium text-muted-foreground">
                                                                                        {action.action === 'create_task' ? 'Task' : 'Termin'}
                                                                                    </span>
                                                                                </div>
                                                                                <div className="flex items-center gap-1">
                                                                                    <button 
                                                                                        onClick={() => editSingleAction(action, idx)}
                                                                                        className="p-1 rounded hover:bg-background/80 text-muted-foreground hover:text-foreground"
                                                                                        title="Im Detail bearbeiten"
                                                                                    >
                                                                                        <Edit2 className="w-3 h-3" />
                                                                                    </button>
                                                                                    <button 
                                                                                        onClick={() => removeMultiAction(idx)}
                                                                                        className="p-1 rounded hover:bg-red-500/10 text-muted-foreground hover:text-red-500"
                                                                                        title="Entfernen"
                                                                                    >
                                                                                        <X className="w-3 h-3" />
                                                                                    </button>
                                                                                </div>
                                                                            </div>

                                                                            {/* Action Details */}
                                                                            <div className="space-y-1.5 text-sm">
                                                                                <div className="font-medium">{action.data?.title}</div>
                                                                                
                                                                                {/* Task specific fields */}
                                                                                {action.action === 'create_task' && (
                                                                                    <div className="flex flex-wrap gap-2 text-xs">
                                                                                        {action.data?.assigned_to_name && (
                                                                                            <span className="px-2 py-0.5 bg-background rounded-full">
                                                                                                👤 {action.data.assigned_to_name}
                                                                                            </span>
                                                                                        )}
                                                                                        {action.data?.deadline && (
                                                                                            <span className="px-2 py-0.5 bg-background rounded-full">
                                                                                                📅 {action.data.deadline}
                                                                                            </span>
                                                                                        )}
                                                                                        {action.data?.priority && (
                                                                                            <Badge variant={action.data.priority === 'high' ? 'destructive' : 'secondary'} className="text-xs h-5">
                                                                                                {action.data.priority === 'high' ? 'Hoch' : action.data.priority === 'low' ? 'Niedrig' : 'Mittel'}
                                                                                            </Badge>
                                                                                        )}
                                                                                    </div>
                                                                                )}
                                                                                
                                                                                {/* Event specific fields */}
                                                                                {action.action === 'create_event' && (
                                                                                    <>
                                                                                        <div className="flex flex-wrap gap-2 text-xs">
                                                                                            {action.data?.start_date && (
                                                                                                <span className="px-2 py-0.5 bg-background rounded-full">
                                                                                                    📅 {action.data.start_date}
                                                                                                </span>
                                                                                            )}
                                                                                            {action.data?.start_time && (
                                                                                                <span className="px-2 py-0.5 bg-background rounded-full">
                                                                                                    🕐 {action.data.start_time} - {action.data.end_time || '?'}
                                                                                                </span>
                                                                                            )}
                                                                                            {action.data?.location && (
                                                                                                <span className="px-2 py-0.5 bg-background rounded-full">
                                                                                                    📍 {action.data.location}
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                        
                                                                                        {/* Calendar selection for events */}
                                                                                        {availableCalendars.length > 0 && (
                                                                                            <div className="mt-2 pt-2 border-t border-border/50">
                                                                                                <select
                                                                                                    value={action.data?.target_calendar || ''}
                                                                                                    onChange={(e) => updateMultiAction(idx, 'target_calendar', e.target.value)}
                                                                                                    className="w-full text-xs p-1.5 rounded-lg border bg-background"
                                                                                                >
                                                                                                    <option value="">Standard-Kalender</option>
                                                                                                    {availableCalendars.map((cal) => (
                                                                                                        <option key={cal.id} value={cal.name}>{cal.name}</option>
                                                                                                    ))}
                                                                                                </select>
                                                                                            </div>
                                                                                        )}
                                                                                    </>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="flex gap-2">
                                                        {result.action !== 'multi_action' && (
                                                            <Button variant="outline" onClick={handleEdit} className="flex-1">
                                                                <Edit2 className="w-4 h-4 mr-2" /> Bearbeiten
                                                            </Button>
                                                        )}
                                                        <Button 
                                                            onClick={result.action === 'multi_action' ? executeMultiActions : executeAction} 
                                                            disabled={isProcessing || (result.action === 'multi_action' && multiActions.length === 0)}
                                                            className={`${result.action === 'multi_action' ? 'w-full' : 'flex-1'} text-white`}
                                                            style={{ background: 'linear-gradient(135deg, hsl(199, 100%, 50%) 0%, hsl(199, 100%, 40%) 100%)' }}
                                                        >
                                                            {isProcessing ? (
                                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                            ) : (
                                                                <Check className="w-4 h-4 mr-2" />
                                                            )}
                                                            {result.action === 'multi_action' 
                                                                ? `${multiActions.length} Aktionen erstellen`
                                                                : result.action === 'time_tracking' && result.data.operation === 'start' ? 'Starten' 
                                                                : result.action === 'time_tracking' && result.data.operation === 'stop' ? 'Stoppen' 
                                                                : result.action === 'vacation_request' ? 'Beantragen' : 'Erstellen'}
                                                        </Button>
                                                    </div>
                                                </>
                                            )}
                                        </motion.div>
                                    )}

                                    {step === 'loading' && (
                                        <motion.div
                                            key="loading"
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            className="py-8 text-center"
                                        >
                                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-500/20 flex items-center justify-center">
                                                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                                            </div>
                                            <p className="font-medium text-muted-foreground">Wird erstellt...</p>
                                        </motion.div>
                                    )}

                                    {step === 'success' && (
                                        <motion.div
                                            key="success"
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            className="py-8 text-center"
                                        >
                                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                                                <Check className="w-8 h-8 text-green-500" />
                                            </div>
                                            <p className="font-medium text-green-600">Erfolgreich erstellt!</p>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {/* Social Face Detail Popup */}
            <Dialog open={!!selectedFace} onOpenChange={() => setSelectedFace(null)}>
                <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
                    {selectedFace && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                    <Users className="w-5 h-5 text-pink-500" />
                                    Social Face Details
                                </DialogTitle>
                            </DialogHeader>
                            
                            <div className="space-y-4">
                                {/* Profile Image */}
                                <div className="flex justify-center">
                                    {selectedFace.profile_image ? (
                                        <img 
                                            src={selectedFace.profile_image} 
                                            alt={`${selectedFace.first_name} ${selectedFace.last_name}`}
                                            className="w-32 h-32 rounded-2xl object-cover ring-4 ring-pink-200 dark:ring-pink-800 shadow-lg"
                                        />
                                    ) : (
                                        <div className="w-32 h-32 rounded-2xl bg-gradient-to-br from-pink-100 to-purple-100 dark:from-pink-900/30 dark:to-purple-900/30 flex items-center justify-center ring-4 ring-pink-200 dark:ring-pink-800">
                                            <Users className="w-16 h-16 text-pink-400" />
                                        </div>
                                    )}
                                </div>

                                {/* Name & Flag */}
                                <div className="text-center">
                                    <h3 className="text-xl font-bold flex items-center justify-center gap-2">
                                        {selectedFace.country_flag && <span className="text-2xl">{selectedFace.country_flag}</span>}
                                        {selectedFace.first_name} {selectedFace.last_name}
                                    </h3>
                                    {selectedFace.city && (
                                        <p className="text-muted-foreground flex items-center justify-center gap-1 mt-1">
                                            <MapPin className="w-4 h-4" />
                                            {selectedFace.city}
                                        </p>
                                    )}
                                </div>

                                {/* Quick Actions */}
                                <div className="flex gap-2 justify-center">
                                    {selectedFace.phone && (
                                        <a 
                                            href={`tel:${selectedFace.phone}`}
                                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-green-500 text-white font-medium hover:bg-green-600 transition-colors"
                                        >
                                            <Phone className="w-4 h-4" />
                                            Anrufen
                                        </a>
                                    )}
                                    {selectedFace.instagram && (
                                        <a 
                                            href={`https://instagram.com/${selectedFace.instagram.replace('@', '')}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium hover:opacity-90 transition-opacity"
                                        >
                                            Instagram
                                        </a>
                                    )}
                                </div>

                                {/* Contact Info */}
                                <div className="p-3 rounded-xl bg-muted/50 space-y-2">
                                    <h4 className="text-sm font-semibold text-muted-foreground">Kontakt</h4>
                                    {selectedFace.phone && (
                                        <div className="flex items-center gap-2 text-sm">
                                            <Phone className="w-4 h-4 text-green-500" />
                                            <span>{selectedFace.phone}</span>
                                        </div>
                                    )}
                                    {selectedFace.email && (
                                        <div className="flex items-center gap-2 text-sm">
                                            <span className="text-blue-500">@</span>
                                            <span>{selectedFace.email}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Details Grid */}
                                <div className="grid grid-cols-2 gap-2">
                                    {selectedFace.gender && (
                                        <div className="p-2 rounded-lg bg-muted/30 text-center">
                                            <p className="text-xs text-muted-foreground">Geschlecht</p>
                                            <p className="font-medium text-sm">{selectedFace.gender}</p>
                                        </div>
                                    )}
                                    {selectedFace.height && (
                                        <div className="p-2 rounded-lg bg-muted/30 text-center">
                                            <p className="text-xs text-muted-foreground">Größe</p>
                                            <p className="font-medium text-sm flex items-center justify-center gap-1">
                                                <Ruler className="w-3 h-3" />
                                                {selectedFace.height} cm
                                            </p>
                                        </div>
                                    )}
                                    {selectedFace.hair_color && (
                                        <div className="p-2 rounded-lg bg-muted/30 text-center">
                                            <p className="text-xs text-muted-foreground">Haarfarbe</p>
                                            <p className="font-medium text-sm">{selectedFace.hair_color}</p>
                                        </div>
                                    )}
                                    {selectedFace.german_level && (
                                        <div className="p-2 rounded-lg bg-muted/30 text-center">
                                            <p className="text-xs text-muted-foreground">Deutsch</p>
                                            <p className="font-medium text-sm capitalize">{selectedFace.german_level}</p>
                                        </div>
                                    )}
                                </div>

                                {/* Tags */}
                                <div className="flex flex-wrap gap-1.5 justify-center">
                                    {selectedFace.tattoos && (
                                        <Badge variant="secondary" className="text-xs">Tattoos</Badge>
                                    )}
                                    {selectedFace.piercings && (
                                        <Badge variant="secondary" className="text-xs">Piercings</Badge>
                                    )}
                                    {selectedFace.glasses && (
                                        <Badge variant="secondary" className="text-xs">Brille</Badge>
                                    )}
                                    {selectedFace.diet && selectedFace.diet !== 'alles' && (
                                        <Badge variant="secondary" className="text-xs capitalize">{selectedFace.diet}</Badge>
                                    )}
                                    {selectedFace.experience_level && (
                                        <Badge className="text-xs bg-pink-500/20 text-pink-600 dark:text-pink-400">
                                            <Sparkles className="w-3 h-3 mr-1" />
                                            {selectedFace.experience_level}
                                        </Badge>
                                    )}
                                </div>

                                {/* Social Media */}
                                {(selectedFace.instagram || selectedFace.tiktok) && (
                                    <div className="p-3 rounded-xl bg-muted/50 space-y-2">
                                        <h4 className="text-sm font-semibold text-muted-foreground">Social Media</h4>
                                        <div className="flex gap-2">
                                            {selectedFace.instagram && selectedFace.instagram.length > 0 && (
                                                <a 
                                                    href={`https://instagram.com/${String(selectedFace.instagram).replace('@', '')}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-sm text-pink-500 hover:underline"
                                                >
                                                    @{String(selectedFace.instagram).replace('@', '')}
                                                </a>
                                            )}
                                            {selectedFace.tiktok && selectedFace.tiktok.length > 0 && (
                                                <a 
                                                    href={`https://tiktok.com/@${String(selectedFace.tiktok).replace('@', '')}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-sm text-blue-500 hover:underline"
                                                >
                                                    TikTok: @{String(selectedFace.tiktok).replace('@', '')}
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default AIAssistant;
